/**
 * Public Monero node configuration for Cashero.
 *
 * Provides a curated list of public Monero daemon RPC endpoints that support CORS
 * and can be used by the browser-based WASM wallet for syncing and transactions.
 */

/** A Monero public node entry */
export interface MoneroNode {
  /** Display name */
  name: string;
  /** The daemon RPC URL (without protocol) */
  host: string;
  /** Port number */
  port: number;
  /** Whether to use HTTPS */
  useTls: boolean;
  /** Whether the node is currently healthy (checked at runtime) */
  healthy?: boolean;
  /** Last checked timestamp */
  lastChecked?: number;
  /** Block height at last check */
  height?: number;
}

/**
 * Default presets — user's top 3 choices.
 * xmr-node.cakewallet.com:18081 as default.
 */
export const DEFAULT_NODES: MoneroNode[] = [
  {
    name: 'CakeWallet',
    host: 'xmr-node.cakewallet.com',
    port: 18081,
    useTls: false,
  },
  {
    name: 'HashVault',
    host: 'nodes.hashvault.pro',
    port: 18081,
    useTls: false,
  },
  {
    name: 'sethforprivacy',
    host: 'node.sethforprivacy.com',
    port: 443,
    useTls: true,
  },
];

/** Get the full URL for a Monero node */
export function getNodeUrl(node: MoneroNode): string {
  const protocol = node.useTls ? 'https' : 'http';
  return `${protocol}://${node.host}:${node.port}`;
}

/** CORS proxy URL (Shakespeare's built-in proxy) */
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Timeout for node health checks (ms) */
const HEALTH_CHECK_TIMEOUT = 10000;

/**
 * Fetch the height of a Monero node via GET /get_height through the CORS proxy.
 * Uses GET instead of POST JSON-RPC for maximum proxy compatibility.
 *
 * @param node - The node to query
 * @returns The node's current block height, or 0 if unavailable
 */
async function fetchNodeHeight(node: MoneroNode): Promise<number> {
  const targetUrl = encodeURIComponent(`${getNodeUrl(node)}/get_height`);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

  try {
    const response = await fetch(CORS_PROXY + targetUrl, {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!response.ok) return 0;
    const data = await response.json();
    return data?.height ?? 0;
  } catch {
    clearTimeout(timeout);
    return 0;
  }
}

/**
 * Check if a Monero node is healthy by querying its height.
 */
export async function checkNodeHealth(node: MoneroNode): Promise<boolean> {
  const height = await fetchNodeHeight(node);
  return height > 0;
}

/**
 * Get the current height of a Monero node.
 */
export async function getNodeHeight(node: MoneroNode): Promise<number> {
  return fetchNodeHeight(node);
}

/**
 * Check all nodes and return their health status.
 */
export async function checkAllNodesHealth(
  nodes: MoneroNode[],
): Promise<MoneroNode[]> {
  const results = await Promise.allSettled(
    nodes.map(async (node) => {
      const height = await getNodeHeight(node);
      return {
        ...node,
        healthy: height > 0,
        height,
        lastChecked: Date.now(),
      };
    }),
  );

  return results.map((r) =>
    r.status === 'fulfilled' ? r.value : { ...r.reason, healthy: false },
  );
}
